import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, btnPrimary } from '../styles/theme.js'
import { rubrics as rubricsApi, evaluations as evalsApi } from '../lib/api.js'

// --- Styles ---

const panelWrap = css`
  font-size: ${font.size.sm};
`

const chipRow = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${space.xs};
  margin-bottom: ${space.md};
`

const chipStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: ${font.size.sm};
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  cursor: pointer;
  &:hover { background: ${colors.bg}; }
`

const chipProgress = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-left: 2px;
`

const chipRemoveBtn = css`
  border: none;
  background: none;
  color: ${colors.textMuted};
  cursor: pointer;
  font-size: ${font.size.sm};
  padding: 0 2px;
  line-height: 1;
  &:hover { color: ${colors.brand}; }
`

const addChip = css`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: ${font.size.sm};
  border: 1px dashed ${colors.border};
  color: ${colors.textMuted};
  cursor: pointer;
  &:hover { border-color: ${colors.brand}; color: ${colors.text}; }
`

const dropdownStyle = css`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  margin-bottom: ${space.md};
  max-height: 200px;
  overflow-y: auto;
`

const dropdownItem = css`
  padding: ${space.sm} ${space.md};
  font-size: ${font.size.sm};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:hover { background: ${colors.bg}; }
`

const dropdownMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const accordionSection = css`
  border: 1px solid ${colors.border};
  border-radius: 6px;
  margin-bottom: ${space.sm};
  overflow: hidden;
`

const accordionHeader = css`
  padding: ${space.sm} ${space.md};
  background: ${colors.bg};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${space.sm};
  font-size: ${font.size.sm};
  user-select: none;
  &:hover { background: ${colors.border}; }
`

const accordionArrow = css`
  font-size: 10px;
  color: ${colors.textMuted};
  width: 12px;
`

const accordionTitle = css`
  flex: 1;
  font-weight: ${font.weight.medium};
`

const accordionProgressStyle = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const accordionBody = css`
  padding: ${space.sm} ${space.md};
  border-top: 1px solid ${colors.border};
`

const criterionItem = css`
  padding: ${space.sm} 0;
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const criterionHeader = css`
  display: flex;
  align-items: flex-start;
  gap: ${space.sm};
  margin-bottom: ${space.xs};
`

const criticalityDot = css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
`

const criterionText = css`
  flex: 1;
  line-height: 1.4;
`

const gradeRow = css`
  display: flex;
  gap: 3px;
  margin-top: ${space.xs};
`

const gradeBtn = css`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  color: ${colors.textSecondary};
  cursor: pointer;
  transition: all 0.1s ease;
  &:hover { background: ${colors.bg}; }
`

const notesInput = css`
  width: 100%;
  padding: ${space.xs} ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  margin-top: ${space.xs};
  resize: vertical;
  min-height: 28px;
`

const saveRow = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.md};
  padding-top: ${space.md};
  border-top: 1px solid ${colors.border};
`

const saveBtn = css`
  ${btnPrimary};
  font-size: ${font.size.xs};
  padding: ${space.xs} ${space.md};
`

const versionNoteStyle = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: ${space.xs};
`

const versionMismatchStyle = css`
  font-size: ${font.size.xs};
  color: ${colors.important};
  background: color-mix(in srgb, ${colors.important} 8%, transparent);
  border: 1px solid color-mix(in srgb, ${colors.important} 25%, transparent);
  border-radius: 4px;
  padding: ${space.xs} ${space.sm};
  margin-bottom: ${space.sm};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.sm};
`

const reEvalBtn = css`
  font-size: ${font.size.xs};
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid ${colors.important};
  background: transparent;
  color: ${colors.important};
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: color-mix(in srgb, ${colors.important} 12%, transparent); }
`

const recoveryBanner = css`
  font-size: ${font.size.xs};
  color: ${colors.brand};
  background: color-mix(in srgb, ${colors.brand} 8%, transparent);
  border: 1px solid color-mix(in srgb, ${colors.brand} 25%, transparent);
  border-radius: 4px;
  padding: ${space.xs} ${space.sm};
  margin-bottom: ${space.sm};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.sm};
`

const recoverBtn = css`
  font-size: ${font.size.xs};
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid ${colors.brand};
  background: transparent;
  color: ${colors.brand};
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: color-mix(in srgb, ${colors.brand} 12%, transparent); }
`

const emptyStyle = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  font-style: italic;
`

const confirmDialog = css`
  background: ${colors.bgCard};
  border: 1px solid ${colors.important};
  border-radius: 6px;
  padding: ${space.sm} ${space.md};
  margin-bottom: ${space.md};
  font-size: ${font.size.sm};
`

const confirmBtns = css`
  display: flex;
  gap: ${space.sm};
  margin-top: ${space.sm};
`

const cancelBtn = css`
  padding: ${space.xs} ${space.md};
  font-size: ${font.size.xs};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
`

const dangerBtn = css`
  padding: ${space.xs} ${space.md};
  font-size: ${font.size.xs};
  border: 1px solid ${colors.critical};
  border-radius: 4px;
  background: ${colors.critical};
  color: white;
  cursor: pointer;
`

export const GRADE_COLORS = {
  yes: '#4ecdc4',
  no: '#ff5c5c',
  partial: '#ffa94d',
  unclear: '#c792ea',
  na: '#999999',
  ungraded: colors.border,
}

export const CRITICALITY_COLORS = {
  critical: colors.critical,
  important: colors.important,
  info: colors.info,
}

export const FULFILLMENT_LABELS = {
  yes: 'Yes',
  no: 'No',
  partial: 'Partial',
  unclear: 'Unclear',
  na: 'N/A',
  ungraded: '\u2014',
}

// Build a default grades map from criteria
export function buildDefaultGrades(criteria) {
  const grades = {}
  for (const c of criteria) {
    grades[c.id] = { fulfilled: 'ungraded', notes: '' }
  }
  return grades
}

// Merge existing evaluation grades into the grades map
export function mergeExistingGrades(grades, existingGrades) {
  const merged = { ...grades }
  for (const g of existingGrades) {
    if (merged[g.criteria_id]) {
      merged[g.criteria_id] = {
        fulfilled: g.fulfilled,
        notes: g.notes || '',
      }
    }
  }
  return merged
}

// EvalPanel component — multi-rubric with chips and accordion.
// Props:
//   projectId      - current project
//   sessionId      - current session ID
//   rubricList     - array of all rubric summaries from listRubrics
//   sessionRubrics - array of rubrics applied to this session
//   _sessionKey    - session ID for change detection
//   onAddRubric(rubricId)    - callback to add rubric to session
//   onRemoveRubric(rubricId) - callback to remove rubric from session
export function EvalPanel() {
  let existingEvals = []
  let rubricDetails = {}   // rubricId -> { id, title, criteria, current_version_id, ... }
  let gradesMap = {}        // rubricId -> { criterionId: { fulfilled, notes } }
  let activeVersionMap = {} // rubricId -> { versionId, version, isCurrent }
  let dirtyMap = {}         // rubricId -> boolean
  let savingMap = {}        // rubricId -> boolean
  let expandedRubrics = new Set() // rubricIds of expanded accordions
  let showAddDropdown = false
  let confirmRemove = null  // rubricId pending removal confirmation
  let recoveryMap = {}      // rubricId -> { grades: [...], rubricVersionId, version } | null

  async function loadExistingEvals(projectId, sessionId) {
    try {
      existingEvals = await evalsApi.list(projectId, sessionId)
    } catch {
      existingEvals = []
    }
  }

  async function loadRubricDetail(projectId, rubricId) {
    if (rubricDetails[rubricId]) return
    try {
      const detail = await rubricsApi.get(projectId, rubricId)
      rubricDetails[rubricId] = detail
      const existing = existingEvals.find(e => e.rubric_id === rubricId)

      let criteria = detail.criteria || []
      let versionId = detail.current_version_id
      let versionNum = detail.versions?.[0]?.version || 1
      let isCurrent = true

      // If existing evaluation was graded against a different version,
      // load that version's criteria so grades map correctly
      if (existing && existing.rubric_version_id !== detail.current_version_id) {
        try {
          const oldVersion = await rubricsApi.getVersion(projectId, existing.rubric_version_id)
          if (oldVersion && oldVersion.criteria) {
            criteria = oldVersion.criteria
            versionId = existing.rubric_version_id
            versionNum = oldVersion.version || existing.version
            isCurrent = false
          }
        } catch (err) {
          console.warn('Failed to load evaluation version, using current:', err)
        }
      } else if (existing) {
        versionNum = existing.version || versionNum
      }

      activeVersionMap[rubricId] = {
        versionId,
        version: versionNum,
        isCurrent,
        currentVersionId: detail.current_version_id,
        currentVersion: detail.versions?.[0]?.version || 1,
      }

      let grades = buildDefaultGrades(criteria)
      if (existing) {
        grades = mergeExistingGrades(grades, existing.grades || [])
      }
      gradesMap[rubricId] = grades
      dirtyMap[rubricId] = false

      // Store the active criteria on the detail for rendering
      rubricDetails[rubricId]._activeCriteria = criteria

      m.redraw()

      // If all grades are ungraded and there's a version mismatch,
      // check if we can recover previous grade data from audit log
      if (!activeVersionMap[rubricId].isCurrent || Object.values(grades).every(g => g.fulfilled === 'ungraded')) {
        checkRecovery(projectId, sessionId, rubricId)
      }
    } catch (err) {
      console.error('Failed to load rubric detail:', err)
    }
  }

  async function checkRecovery(projectId, sessionId, rubricId) {
    const grades = gradesMap[rubricId] || {}
    const allUngraded = Object.values(grades).every(g => g.fulfilled === 'ungraded')
    if (!allUngraded) return

    try {
      const candidates = await evalsApi.getRecovery(projectId, sessionId)
      const match = candidates.find(c => c.rubricId === rubricId)
      if (match) {
        recoveryMap[rubricId] = match
        m.redraw()
      }
    } catch {
      // Recovery is best-effort, don't block on failure
    }
  }

  async function restoreFromRecovery(projectId, rubricId) {
    const recovery = recoveryMap[rubricId]
    if (!recovery) return

    // Load the version criteria that the recovery data was graded against
    try {
      const version = await rubricsApi.getVersion(projectId, recovery.rubricVersionId)
      if (!version || !version.criteria) return

      const criteria = version.criteria
      let grades = buildDefaultGrades(criteria)
      // Map recovered grades back
      for (const g of recovery.grades) {
        if (grades[g.criteriaId]) {
          grades[g.criteriaId] = { fulfilled: g.fulfilled, notes: g.notes || '' }
        }
      }
      gradesMap[rubricId] = grades
      activeVersionMap[rubricId] = {
        versionId: recovery.rubricVersionId,
        version: version.version || recovery.version,
        isCurrent: recovery.rubricVersionId === rubricDetails[rubricId]?.current_version_id,
        currentVersionId: rubricDetails[rubricId]?.current_version_id,
        currentVersion: rubricDetails[rubricId]?.versions?.[0]?.version || 1,
      }
      rubricDetails[rubricId]._activeCriteria = criteria
      dirtyMap[rubricId] = true
      recoveryMap[rubricId] = null
      m.redraw()
    } catch (err) {
      console.error('Failed to restore from recovery:', err)
    }
  }

  function switchToCurrentVersion(rubricId) {
    const detail = rubricDetails[rubricId]
    if (!detail) return
    const criteria = detail.criteria || []
    gradesMap[rubricId] = buildDefaultGrades(criteria)
    dirtyMap[rubricId] = true
    activeVersionMap[rubricId] = {
      versionId: detail.current_version_id,
      version: detail.versions?.[0]?.version || 1,
      isCurrent: true,
      currentVersionId: detail.current_version_id,
      currentVersion: detail.versions?.[0]?.version || 1,
    }
    rubricDetails[rubricId]._activeCriteria = criteria
    recoveryMap[rubricId] = null
  }

  function setGrade(rubricId, criterionId, fulfilled) {
    if (!gradesMap[rubricId]) return
    gradesMap[rubricId][criterionId] = { ...gradesMap[rubricId][criterionId], fulfilled }
    dirtyMap[rubricId] = true
  }

  function setNotes(rubricId, criterionId, notes) {
    if (!gradesMap[rubricId]) return
    gradesMap[rubricId][criterionId] = { ...gradesMap[rubricId][criterionId], notes }
    dirtyMap[rubricId] = true
  }

  async function saveGradesForRubric(projectId, sessionId, rubricId) {
    const detail = rubricDetails[rubricId]
    if (!detail) return
    savingMap[rubricId] = true
    m.redraw()
    try {
      const grades = gradesMap[rubricId] || {}
      const verInfo = activeVersionMap[rubricId]
      const payload = {
        rubricId,
        rubricVersionId: verInfo?.versionId || detail.current_version_id,
        grades: Object.entries(grades).map(([criteriaId, g]) => ({
          criteriaId,
          fulfilled: g.fulfilled,
          notes: g.notes || undefined,
        })),
      }
      await evalsApi.save(projectId, sessionId, payload)
      dirtyMap[rubricId] = false
      await loadExistingEvals(projectId, sessionId)
    } catch (err) {
      console.error('Failed to save evaluation:', err)
    } finally {
      savingMap[rubricId] = false
      m.redraw()
    }
  }

  function getProgress(rubricId) {
    const detail = rubricDetails[rubricId]
    if (!detail) return { graded: 0, total: 0 }
    const criteria = detail._activeCriteria || detail.criteria || []
    const grades = gradesMap[rubricId] || {}
    const graded = Object.values(grades).filter(g => g.fulfilled !== 'ungraded').length
    return { graded, total: criteria.length }
  }

  function initForSession(projectId, sessionId, sessionRubrics) {
    loadExistingEvals(projectId, sessionId).then(() => {
      for (const sr of (sessionRubrics || [])) {
        loadRubricDetail(projectId, sr.rubric_id)
      }
      // Default all applied rubrics to expanded
      for (const sr of (sessionRubrics || [])) {
        expandedRubrics.add(sr.rubric_id)
      }
      m.redraw()
    })
  }

  return {
    oninit(vnode) {
      const { projectId, sessionId, sessionRubrics } = vnode.attrs
      vnode.state._prevKey = vnode.attrs._sessionKey
      vnode.state._rubricIds = (sessionRubrics || []).map(sr => sr.rubric_id).sort().join(',')
      initForSession(projectId, sessionId, sessionRubrics)
    },

    onupdate(vnode) {
      const { projectId, sessionId, sessionRubrics } = vnode.attrs

      // Session changed — reset everything
      if (vnode.attrs._sessionKey !== vnode.state._prevKey) {
        vnode.state._prevKey = vnode.attrs._sessionKey
        existingEvals = []
        rubricDetails = {}
        gradesMap = {}
        activeVersionMap = {}
        dirtyMap = {}
        savingMap = {}
        expandedRubrics = new Set()
        confirmRemove = null
        showAddDropdown = false
        recoveryMap = {}
        vnode.state._rubricIds = (sessionRubrics || []).map(sr => sr.rubric_id).sort().join(',')
        initForSession(projectId, sessionId, sessionRubrics)
        return
      }

      // Session rubrics changed (rubric added/removed externally)
      const currentIds = (sessionRubrics || []).map(sr => sr.rubric_id).sort().join(',')
      if (currentIds !== vnode.state._rubricIds) {
        vnode.state._rubricIds = currentIds
        for (const sr of (sessionRubrics || [])) {
          loadRubricDetail(projectId, sr.rubric_id)
        }
      }
    },

    view(vnode) {
      const { projectId, sessionId, rubricList, sessionRubrics, onAddRubric, onRemoveRubric } = vnode.attrs

      if (!rubricList || rubricList.length === 0) {
        return m('div', [
          m('div', { class: emptyStyle }, 'No rubrics defined.'),
          m('div', {
            style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; cursor: pointer; margin-top: ${space.sm};`,
            onclick: () => m.route.set(`/projects/${projectId}/rubrics`),
          }, 'Manage rubrics'),
        ])
      }

      const appliedRubrics = sessionRubrics || []
      const availableToAdd = rubricList.filter(
        r => !appliedRubrics.some(sr => sr.rubric_id === r.id)
      )

      // --- Chip row ---
      const chips = []
      for (const sr of appliedRubrics) {
        const hasEval = existingEvals.some(e => e.rubric_id === sr.rubric_id)
        const { graded, total } = getProgress(sr.rubric_id)

        chips.push(m('div', { key: sr.rubric_id, class: chipStyle }, [
          m('span', {
            onclick: () => { expandedRubrics.has(sr.rubric_id) ? expandedRubrics.delete(sr.rubric_id) : expandedRubrics.add(sr.rubric_id) },
          }, [
            sr.title,
            total > 0
              ? m('span', { class: chipProgress }, ` ${graded}/${total}`)
              : null,
          ]),
          m('button', {
            class: chipRemoveBtn,
            onclick: (e) => {
              e.stopPropagation()
              if (hasEval || dirtyMap[sr.rubric_id]) {
                confirmRemove = sr.rubric_id
              } else {
                onRemoveRubric(sr.rubric_id)
              }
            },
            title: 'Remove rubric',
          }, '\u00d7'),
        ]))
      }
      if (availableToAdd.length > 0) {
        chips.push(m('div', {
          key: '__add__',
          class: addChip,
          onclick: () => { showAddDropdown = !showAddDropdown },
        }, '+ Add'))
      }

      // --- Build accordion sections ---
      const sections = []
      for (const sr of appliedRubrics) {
        const rubricId = sr.rubric_id
        const isExpanded = expandedRubrics.has(rubricId)
        const detail = rubricDetails[rubricId]
        const criteria = detail?._activeCriteria || detail?.criteria || []
        const grades = gradesMap[rubricId] || {}
        const dirty = dirtyMap[rubricId] || false
        const saving = savingMap[rubricId] || false
        const existingEval = existingEvals.find(e => e.rubric_id === rubricId)
        const { graded, total } = getProgress(rubricId)

        // Build body children imperatively to avoid keyed/non-keyed mix
        const bodyChildren = []
        if (isExpanded && detail) {
          const verInfo = activeVersionMap[rubricId]
          const recovery = recoveryMap[rubricId]

          // Version mismatch banner
          if (verInfo && !verInfo.isCurrent && existingEval) {
            bodyChildren.push(m('div', { key: '__mismatch__', class: versionMismatchStyle }, [
              m('span', `Evaluated against v${verInfo.version} (current: v${verInfo.currentVersion})`),
              m('button', {
                class: reEvalBtn,
                onclick: () => { switchToCurrentVersion(rubricId) },
              }, 'Re-evaluate with current version'),
            ]))
          }

          // Recovery banner
          if (recovery) {
            bodyChildren.push(m('div', { key: '__recovery__', class: recoveryBanner }, [
              m('span', `Previous grades from v${recovery.version} can be restored`),
              m('button', {
                class: recoverBtn,
                onclick: () => { restoreFromRecovery(projectId, rubricId) },
              }, 'Restore previous grades'),
            ]))
          }

          for (const criterion of criteria) {
            bodyChildren.push(m('div', { key: criterion.id, class: criterionItem }, [
              m('div', { class: criterionHeader }, [
                m('div', {
                  class: criticalityDot,
                  style: `background: ${CRITICALITY_COLORS[criterion.criticality]};`,
                  title: criterion.criticality,
                }),
                m('div', { class: criterionText }, criterion.description),
              ]),
              m('div', { class: gradeRow },
                ['yes', 'no', 'partial', 'unclear', 'na'].map(val =>
                  m('button', {
                    key: val,
                    class: gradeBtn,
                    style: grades[criterion.id]?.fulfilled === val
                      ? `background: ${GRADE_COLORS[val]}; color: white; border-color: ${GRADE_COLORS[val]};`
                      : '',
                    onclick: () => setGrade(rubricId, criterion.id, val),
                  }, FULFILLMENT_LABELS[val])
                ),
              ),
              m('textarea', {
                class: notesInput,
                placeholder: 'Notes...',
                value: grades[criterion.id]?.notes || '',
                oninput: (e) => setNotes(rubricId, criterion.id, e.target.value),
                rows: 1,
              }),
            ]))
          }
          bodyChildren.push(m('div', { key: '__version__', class: versionNoteStyle },
            existingEval
              ? `Graded against v${verInfo?.version || existingEval.version}`
              : ''))
          bodyChildren.push(m('div', { key: '__save__', class: saveRow }, [
            m('button', {
              class: saveBtn,
              disabled: saving || !dirty,
              onclick: () => saveGradesForRubric(projectId, sessionId, rubricId),
            }, saving ? 'Saving...' : (existingEval ? 'Update' : 'Save')),
          ]))
        }

        sections.push(m('div', { key: rubricId, class: accordionSection }, [
          m('div', {
            class: accordionHeader,
            onclick: () => { isExpanded ? expandedRubrics.delete(rubricId) : expandedRubrics.add(rubricId) },
          }, [
            m('span', { class: accordionArrow }, isExpanded ? '\u25BC' : '\u25B6'),
            m('span', { class: accordionTitle }, sr.title),
            m('span', { class: accordionProgressStyle },
              total > 0 ? `${graded}/${total}` : '\u2026'),
          ]),
          isExpanded && detail
            ? m('div', { class: accordionBody }, bodyChildren)
            : null,
        ]))
      }

      // --- Assemble view ---
      const content = []
      content.push(m('div', { key: '__chips__', class: chipRow }, chips))

      // Removal confirmation dialog
      if (confirmRemove) {
        const rubricName = appliedRubrics.find(sr => sr.rubric_id === confirmRemove)?.title || 'this rubric'
        const hasSavedEval = existingEvals.some(e => e.rubric_id === confirmRemove)
        const warningText = hasSavedEval
          ? `Removing "${rubricName}" will delete its evaluation data.`
          : `You have unsaved grades for "${rubricName}". Remove anyway?`
        content.push(m('div', { key: '__confirm__', class: confirmDialog }, [
          m('div', warningText),
          m('div', { class: confirmBtns }, [
            m('button', { class: cancelBtn, onclick: () => { confirmRemove = null } }, 'Cancel'),
            m('button', {
              class: dangerBtn,
              onclick: () => {
                onRemoveRubric(confirmRemove)
                confirmRemove = null
              },
            }, 'Remove'),
          ]),
        ]))
      }

      // Add dropdown
      if (showAddDropdown) {
        const items = availableToAdd.map(r =>
          m('div', {
            key: r.id,
            class: dropdownItem,
            onclick: () => {
              onAddRubric(r.id)
              showAddDropdown = false
            },
          }, [
            r.title,
            m('span', { class: dropdownMeta }, `${r.criteria_count || 0} criteria`),
          ])
        )
        if (items.length === 0) {
          items.push(m('div', {
            key: '__none__',
            class: dropdownItem,
            style: `color: ${colors.textMuted}; font-style: italic; cursor: default;`,
          }, 'All rubrics added'))
        }
        content.push(m('div', { key: '__dropdown__', class: dropdownStyle }, items))
      }

      // Accordion sections
      if (appliedRubrics.length > 0) {
        content.push(m('div', { key: '__sections__' }, sections))
      } else {
        content.push(m('div', { key: '__empty__', class: emptyStyle },
          'No rubrics applied. Add a rubric to start evaluating.'))
      }

      return m('div', { class: panelWrap }, content)
    },
  }
}
